import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, LogIn, Store, ShieldCheck, BarChart3, Package, AlertCircle, Loader2 } from 'lucide-react';
import { isCajeroUser } from '../utils/userRoles';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [focusedField, setFocusedField] = useState(null);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        
        const res = await login(email, password);
        
        if (res.success) {
            setIsLoading(false);
            if (isCajeroUser(res.user)) {
                navigate(res.requiresOpenCaja ? '/pos?openCaja=1' : '/pos');
            } else {
                navigate('/');
            }
        } else {
            setError(res.error || 'Credenciales incorrectas');
            setIsLoading(false);
        }
    };

    const features = [
        { icon: Package, title: 'Inventario', desc: 'Control total de productos' },
        { icon: BarChart3, title: 'Reportes', desc: 'Análisis en tiempo real' },
        { icon: ShieldCheck, title: 'Seguro', desc: 'Datos protegidos' },
    ];

    return (
        <div style={styles.container}>
            {/* Animated background elements */}
            <div style={styles.bgOrb1}></div>
            <div style={styles.bgOrb2}></div>
            <div style={styles.bgOrb3}></div>
            
            <div style={styles.wrapper}>
                {/* Left Panel - Branding */}
                <div style={styles.brandPanel}>
                    <div style={styles.brandContent}>
                        <div style={styles.logoContainer}>
                            <Store size={48} style={styles.logoIcon} />
                            <div style={styles.logoGlow}></div>
                        </div>
                        <h1 style={styles.brandTitle}>ERP Minimarket</h1>
                        <p style={styles.brandSubtitle}>
                            Sistema integral de gestión para tu negocio
                        </p>
                        
                        <div style={styles.featuresGrid}>
                            {features.map((feature, index) => (
                                <div key={index} style={styles.featureCard}>
                                    <feature.icon size={24} style={styles.featureIcon} />
                                    <div>
                                        <h4 style={styles.featureTitle}>{feature.title}</h4>
                                        <p style={styles.featureDesc}>{feature.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <p style={styles.copyright}>
                        © 2026 ERP Minimarket. Todos los derechos reservados.
                    </p>
                </div>

                {/* Right Panel - Login Form */}
                <div style={styles.formPanel}>
                    <div style={styles.formCard}>
                        {/* Glassmorphism shine effect */}
                        <div style={styles.cardShine}></div>
                        
                        <div style={styles.formHeader}>
                            <h2 style={styles.formTitle}>Bienvenido</h2>
                            <p style={styles.formSubtitle}>
                                Ingresa tus credenciales para acceder al sistema
                            </p>
                        </div>

                        {error && (
                            <div style={styles.errorBox}>
                                <AlertCircle size={18} />
                                <span>{error}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} style={styles.form}>
                            <div style={{
                                ...styles.inputGroup,
                                ...(focusedField === 'email' ? styles.inputGroupFocused : {})
                            }}>
                                <label style={styles.label}>
                                    <Mail size={16} style={styles.labelIcon} />
                                    Correo Electrónico
                                </label>
                                <div style={styles.inputWrapper}>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        onFocus={() => setFocusedField('email')}
                                        onBlur={() => setFocusedField(null)}
                                        placeholder="tu@correo.com"
                                        required
                                        style={styles.input}
                                    />
                                </div>
                            </div>

                            <div style={{
                                ...styles.inputGroup,
                                ...(focusedField === 'password' ? styles.inputGroupFocused : {})
                            }}>
                                <label style={styles.label}>
                                    <Lock size={16} style={styles.labelIcon} />
                                    Contraseña
                                </label>
                                <div style={styles.inputWrapper}>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onFocus={() => setFocusedField('password')}
                                        onBlur={() => setFocusedField(null)}
                                        placeholder="••••••••"
                                        required
                                        style={styles.input}
                                    />
                                </div>
                            </div>

                            <button 
                                type="submit" 
                                disabled={isLoading}
                                style={{
                                    ...styles.submitBtn,
                                    ...(isLoading ? styles.submitBtnLoading : {})
                                }}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 size={20} className="spinning" />
                                        <span>Ingresando...</span>
                                    </>
                                ) : (
                                    <>
                                        <LogIn size={20} />
                                        <span>Ingresar al Sistema</span>
                                    </>
                                )}
                            </button>
                        </form>

                        <div style={styles.helpText}>
                            <p>¿Problemas para acceder? Contacta al administrador</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(165deg, #faf9f7 0%, #f5f3f0 50%, #e8e6e4 100%)',
        padding: '1rem',
        position: 'relative',
        overflow: 'hidden',
    },
    bgOrb1: {
        position: 'absolute',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(245, 183, 0, 0.08) 0%, transparent 70%)',
        top: '-200px',
        right: '-100px',
        animation: 'pulse 8s ease-in-out infinite',
    },
    bgOrb2: {
        position: 'absolute',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(217, 119, 6, 0.06) 0%, transparent 70%)',
        bottom: '-100px',
        left: '-50px',
        animation: 'pulse 10s ease-in-out infinite reverse',
    },
    bgOrb3: {
        position: 'absolute',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(5, 150, 105, 0.05) 0%, transparent 70%)',
        top: '50%',
        left: '30%',
        animation: 'pulse 12s ease-in-out infinite',
    },
    wrapper: {
        display: 'flex',
        width: '100%',
        maxWidth: '1000px',
        minHeight: '600px',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(28, 25, 23, 0.12), 0 0 0 1px rgba(28, 25, 23, 0.04)',
        position: 'relative',
        zIndex: 1,
    },
    brandPanel: {
        flex: '1',
        background: 'linear-gradient(135deg, #0F0F0F 0%, #F5B700 50%, #C28C00 100%)',
        padding: '3rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
    },
    brandContent: {
        position: 'relative',
        zIndex: 1,
    },
    logoContainer: {
        position: 'relative',
        display: 'inline-block',
        marginBottom: '1.5rem',
    },
    logoIcon: {
        color: 'white',
        filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.2))',
    },
    logoGlow: {
        position: 'absolute',
        inset: '-10px',
        background: 'radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(10px)',
    },
    brandTitle: {
        fontSize: '2.5rem',
        fontWeight: '800',
        color: 'white',
        margin: '0 0 0.75rem 0',
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },
    brandSubtitle: {
        fontSize: '1.1rem',
        color: 'rgba(255, 255, 255, 0.9)',
        margin: '0 0 2.5rem 0',
        lineHeight: '1.6',
    },
    featuresGrid: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
    },
    featureCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '1rem',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        transition: 'all 0.3s ease',
    },
    featureIcon: {
        color: 'white',
        flexShrink: 0,
    },
    featureTitle: {
        fontSize: '0.95rem',
        fontWeight: '600',
        color: 'white',
        margin: '0 0 0.25rem 0',
    },
    featureDesc: {
        fontSize: '0.85rem',
        color: 'rgba(255, 255, 255, 0.75)',
        margin: 0,
    },
    copyright: {
        fontSize: '0.8rem',
        color: 'rgba(255, 255, 255, 0.6)',
        margin: 0,
        position: 'relative',
        zIndex: 1,
    },
    formPanel: {
        flex: '1',
        background: 'linear-gradient(180deg, #ffffff 0%, #faf9f7 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
    },
    formCard: {
        width: '100%',
        maxWidth: '380px',
        position: 'relative',
        padding: '2.5rem',
        background: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '20px',
        border: '1px solid rgba(28, 25, 23, 0.06)',
        boxShadow: '0 4px 24px rgba(28, 25, 23, 0.06)',
        overflow: 'hidden',
    },
    cardShine: {
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: 'linear-gradient(45deg, transparent 40%, rgba(245, 183, 0, 0.02) 50%, transparent 60%)',
        animation: 'shine 8s ease-in-out infinite',
        pointerEvents: 'none',
    },
    formHeader: {
        textAlign: 'center',
        marginBottom: '2rem',
        position: 'relative',
        zIndex: 1,
    },
    formTitle: {
        fontFamily: "'Outfit', 'DM Sans', sans-serif",
        fontSize: '1.75rem',
        fontWeight: '700',
        color: '#1c1917',
        margin: '0 0 0.5rem 0',
    },
    formSubtitle: {
        fontSize: '0.95rem',
        color: '#57534e',
        margin: 0,
    },
    errorBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '1rem',
        background: '#fee2e2',
        border: '1px solid rgba(220, 38, 38, 0.3)',
        borderRadius: '12px',
        color: '#b91c1c',
        fontSize: '0.9rem',
        marginBottom: '1.5rem',
        position: 'relative',
        zIndex: 1,
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
        position: 'relative',
        zIndex: 1,
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        transition: 'transform 0.2s ease',
    },
    inputGroupFocused: {
        transform: 'translateY(-2px)',
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontSize: '0.9rem',
        fontWeight: '500',
        color: '#44403c',
    },
    labelIcon: {
        color: '#F5B700',
    },
    inputWrapper: {
        position: 'relative',
    },
    input: {
        width: '100%',
        padding: '0.875rem 1rem',
        fontSize: '1rem',
        color: '#1c1917',
        background: '#f8f6f4',
        border: '1px solid #e7e5e4',
        borderRadius: '12px',
        outline: 'none',
        transition: 'all 0.2s ease',
        boxSizing: 'border-box',
    },
    submitBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.75rem',
        width: '100%',
        padding: '1rem',
        marginTop: '0.5rem',
        fontSize: '1rem',
        fontWeight: '600',
        color: '#141414',
        background: 'linear-gradient(135deg, #F5B700 0%, #C28C00 100%)',
        border: 'none',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 4px 15px rgba(245, 183, 0, 0.35)',
        position: 'relative',
        overflow: 'hidden',
    },
    submitBtnLoading: {
        opacity: 0.8,
        cursor: 'not-allowed',
    },
    helpText: {
        marginTop: '1.5rem',
        textAlign: 'center',
        fontSize: '0.85rem',
        color: '#78716c',
        position: 'relative',
        zIndex: 1,
    },
};
